/** @file Headline usage total above a chart, decomposed into metered + estimated. */

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { theme } from '../../theme';
import { formatNumber } from '../../util';
import { ESTIMATED_SWATCH_BACKGROUND, ESTIMATED_SWATCH_BORDER } from '../Chart/util';

type Props = {
  meteredVolume: number;
  estimatedVolume: number;
};

/**
 * Headline total for a usage chart. The combined total is the single primary
 * number; when estimates exist, a secondary caption line decomposes it. Parts
 * are rounded once to whole gallons and the total is the sum of the DISPLAYED
 * parts, so the arithmetic a reader can check always holds. The estimated part
 * carries the same hatched swatch as the chart legend, binding the number to
 * the gray hatched segment on the chart; metered deliberately gets no swatch
 * since multi-meter units render metered bars in several colors. Periods
 * without estimates render just the plain total, matching pre-feature output.
 *
 * @param {object} props - Component props
 * @param {number} props.meteredVolume - Total metered volume in gallons
 * @param {number} props.estimatedVolume - Total estimated volume in gallons
 * @returns {JSX.Element} - Headline total block
 */
export const UsageTotalHeadline = ({ meteredVolume, estimatedVolume }: Props): JSX.Element => {
  const metered = Math.round(meteredVolume);
  const estimated = Math.round(estimatedVolume);

  if (estimated <= 0) {
    return (
      <Typography variant="h6" align="center" color={theme.palette.grey[700]}>
        {formatNumber(metered)} gal
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="h6" align="center" color={theme.palette.grey[700]}>
        {formatNumber(metered + estimated)} gal
      </Typography>
      <Stack direction="row" justifyContent="center" alignItems="center" gap={0.75} flexWrap="wrap">
        <Typography variant="caption" sx={{ fontSize: 12 }} color={theme.palette.grey[700]}>
          {formatNumber(metered)} gal metered
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 12 }} color={theme.palette.grey[700]}>
          ·
        </Typography>
        <Box
          aria-hidden="true"
          sx={{
            width: 12,
            height: 12,
            borderRadius: '2px',
            border: `1px solid ${ESTIMATED_SWATCH_BORDER}`,
            background: ESTIMATED_SWATCH_BACKGROUND,
            flexShrink: 0,
          }}
        />
        <Typography variant="caption" sx={{ fontSize: 12 }} color={theme.palette.grey[700]}>
          {formatNumber(estimated)} gal estimated
        </Typography>
      </Stack>
    </Box>
  );
};
